import { useEffect, useState, type FormEvent } from "react";
import { BadgeCheck, Copy, KeyRound, LockKeyhole, Send, ShieldCheck, Sparkles } from "lucide-react";
import { api, type AuthChannel, type PublicLoginOptions, type SessionUser } from "./api";

type Mode = "otp" | "password" | "reset";

const DEFAULT_OPTIONS: PublicLoginOptions = { smsOtp: true, emailOtp: true, password: true, register: true };

export function LoginPage({ onDone }: { onDone: () => void }) {
  const [options, setOptions] = useState(DEFAULT_OPTIONS);
  const [mode, setMode] = useState<Mode>("otp");
  const [channel, setChannel] = useState<AuthChannel>("sms");
  const [target, setTarget] = useState("");
  const [code, setCode] = useState("");
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [sent, setSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [welcome, setWelcome] = useState<{ user: SessionUser } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.getLoginOptions().then((loginOptions) => {
      setOptions(loginOptions);
      if (!loginOptions.smsOtp && loginOptions.emailOtp) setChannel("email");
      setMode(loginOptions.smsOtp || loginOptions.emailOtp ? "otp" : "password");
    }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setInterval(() => setCountdown((value) => value - 1), 1_000);
    return () => window.clearInterval(timer);
  }, [countdown]);

  const targetValid = channel === "sms"
    ? /^1\d{10}$/.test(target)
    : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target);
  const otpChannels = [options.smsOtp && "sms", options.emailOtp && "email"].filter(Boolean) as AuthChannel[];
  const showTabs = otpChannels.length + (options.password ? 1 : 0) > 1;
  const channelLabel = (value: AuthChannel) => value === "sms" ? "手机号" : "邮箱";

  function switchMode(nextMode: Mode, nextChannel?: AuthChannel) {
    setMode(nextMode);
    if (nextChannel) setChannel(nextChannel);
    setSent(false);
    setCode("");
    setNewPassword("");
    setMessage("");
  }

  async function requestCode() {
    if (!targetValid) {
      setMessage(channel === "sms" ? "请输入正确的11位手机号" : "请输入正确的邮箱地址");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const result = await api.requestCode({ channel, purpose: mode === "reset" ? "reset" : "login", target });
      setSent(true);
      setCountdown(result.retryAfterSeconds);
      setMessage(result.debugCode ? `开发环境验证码：${result.debugCode}` : "验证码已发送，5分钟内有效");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "验证码发送失败");
    } finally {
      setLoading(false);
    }
  }

  function finishAuthentication() {
    localStorage.setItem("openapi_authenticated", "true");
    onDone();
  }

  async function submitOtp() {
    if (code.length !== 6) return setMessage("请输入6位验证码");
    setLoading(true);
    setMessage("");
    try {
      const result = await api.verifyCode({ channel, purpose: "login", target, code });
      if (result.isNewUser && result.user.memberNo) {
        setWelcome(result);
      } else {
        finishAuthentication();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  async function submitPassword() {
    if (!account.trim() || !password) return setMessage("请输入账号和密码");
    setLoading(true);
    setMessage("");
    try {
      await api.loginPassword(account.trim(), password);
      finishAuthentication();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "登录失败");
    } finally {
      setLoading(false);
    }
  }

  async function submitReset() {
    if (code.length !== 6) return setMessage("请输入6位验证码");
    if (newPassword.length < 8) return setMessage("新密码长度需为8-64位");
    setLoading(true);
    setMessage("");
    try {
      await api.resetPassword({ channel, target, code, newPassword });
      setPassword("");
      switchMode("password");
      setMessage("密码已重置，请使用新密码登录");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "重置失败");
    } finally {
      setLoading(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (mode === "password") return void submitPassword();
    if (!sent) return void requestCode();
    return void (mode === "reset" ? submitReset() : submitOtp());
  }

  async function copyMemberNo() {
    if (!welcome?.user.memberNo) return;
    try {
      await navigator.clipboard.writeText(welcome.user.memberNo);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      setMessage("复制失败，请手动记录会员账号");
    }
  }

  return <main className="login">
    <section className="login-art">
      <div className="brand public-brand"><span><Sparkles size={19} /></span><div><strong>OPEN-API</strong><small>学霸阁开发者平台</small></div></div>
      <div><p>UNIFIED AI GATEWAY</p><h1>一个账号，<br />连接你的 AI 产品</h1><span>与妙想阁主站、数字人平台共用会员账号、积分余额和安全登录能力。</span></div>
      <small><ShieldCheck size={18} />HttpOnly 会话 · 接口密钥仅显示一次 · 失败自动解冻</small>
    </section>
    <section className="login-form-wrap">
      {welcome ? <div className="login-form login-welcome">
        <div className="login-icon"><BadgeCheck /></div>
        <h2>开发者账户已创建</h2>
        <p>这是你的全站通用会员账号，请保存后再进入控制台。</p>
        <div className="member-card"><small>会员账号</small><strong>{welcome.user.memberNo}</strong><button type="button" onClick={copyMemberNo}><Copy size={16} />{copied ? "已复制" : "复制"}</button></div>
        {message ? <p className="form-message" role="status">{message}</p> : null}
        <button className="button primary" type="button" onClick={finishAuthentication}><Sparkles size={18} />进入开发者控制台</button>
      </div> : <form className="login-form" onSubmit={submit}>
        <div className="login-icon">{mode === "password" ? <LockKeyhole /> : <KeyRound />}</div>
        <h2>{mode === "reset" ? "重置密码" : "注册或登录"}</h2>
        <p>{mode === "reset" ? "通过已绑定的手机号或邮箱验证身份并设置新密码。" : options.register ? "首次验证手机号或邮箱会自动创建全站通用账户。" : "使用已绑定的手机号、邮箱或会员账号登录。"}</p>
        <div className="security-note"><ShieldCheck size={18} />本浏览器登录状态保留30天；主动退出后需要重新验证。</div>
        {showTabs ? <div className="login-tabs" role="tablist">
          {otpChannels.map((value) => <button type="button" role="tab" aria-selected={mode !== "password" && channel === value} className={mode !== "password" && channel === value ? "login-tab active" : "login-tab"} key={value} onClick={() => switchMode(mode === "reset" ? "reset" : "otp", value)}>{channelLabel(value)}验证码</button>)}
          {options.password ? <button type="button" role="tab" aria-selected={mode === "password"} className={mode === "password" ? "login-tab active" : "login-tab"} onClick={() => switchMode("password")}>账号密码</button> : null}
        </div> : null}
        {mode === "password" ? <>
          <label>会员账号 / 手机号 / 邮箱<input autoComplete="username" value={account} placeholder="任一账号均可" onChange={(event) => setAccount(event.target.value)} /></label>
          <label>密码<input autoComplete="current-password" type="password" value={password} placeholder="请输入密码" onChange={(event) => setPassword(event.target.value)} /></label>
          {otpChannels.length ? <button className="login-link" type="button" onClick={() => switchMode("reset")}>忘记密码？</button> : null}
        </> : <>
          <label>{channelLabel(channel)}<input autoComplete={channel === "sms" ? "tel" : "email"} value={target} inputMode={channel === "sms" ? "numeric" : "email"} maxLength={channel === "sms" ? 11 : 160} placeholder={mode === "reset" ? `请输入已绑定的${channelLabel(channel)}` : `请输入${channelLabel(channel)}`} onChange={(event) => setTarget(channel === "sms" ? event.target.value.replace(/\D/g, "") : event.target.value.trim())} /></label>
          {sent ? <label>验证码<input autoComplete="one-time-code" value={code} inputMode="numeric" maxLength={6} placeholder="6位验证码" onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} /></label> : null}
          {mode === "reset" && sent ? <label>新密码<input autoComplete="new-password" type="password" maxLength={64} value={newPassword} placeholder="8-64位新密码" onChange={(event) => setNewPassword(event.target.value)} /></label> : null}
        </>}
        {message ? <p className="form-message" role="status">{message}</p> : null}
        <button className="button primary" disabled={loading || (sent && mode === "otp" && code.length !== 6) || (sent && mode === "reset" && (code.length !== 6 || newPassword.length < 8))}>{loading ? "请稍候…" : mode === "password" ? "登录" : !sent ? "获取验证码" : mode === "reset" ? "重置密码" : "进入控制台"}<Send size={18} /></button>
        {sent ? <button className="text-button resend-countdown" type="button" disabled={countdown > 0 || loading} onClick={requestCode}>{countdown > 0 ? `${countdown} 秒后可重发` : "重新发送验证码"}</button> : null}
        <small className="login-legal">会员账号、积分余额和登录凭据在三个平台通用。</small>
      </form>}
    </section>
  </main>;
}
